import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, Select } from '@caios/ui';

type Status = { id: string; name: string; category: string; color: string | null; position: number };
const CAT: Record<string, string> = { not_started: 'Not started', active: 'Active', done: 'Done', closed: 'Closed' };
const send = async (path: string, method: string, body?: unknown) => {
  const r = await fetch(path, { method, credentials: 'include', ...(body ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? 'That didn’t work.');
  return j;
};

/** Managers: a space's statuses. Categories are fixed once created (history and completion depend on them). */
export function StatusEditor({ spaceId, onClose }: { spaceId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ops-statuses', spaceId], queryFn: () => send(`/api/v1/ops/spaces/${spaceId}/statuses`, 'GET') as Promise<Status[]> });
  const [name, setName] = useState('');
  const [category, setCategory] = useState('active');
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['ops-statuses', spaceId] }); qc.invalidateQueries({ queryKey: ['ops-list'] }); };
  const run = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); refresh(); } catch (e) { setErr((e as Error).message); } };
  const list = q.data ?? [];
  return (
    <div className="cx-inset status-editor cx-rise" data-testid="status-editor">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><b>Statuses</b><button type="button" className="cx-link-btn" onClick={onClose}>Close</button></div>
      <ul className="cx-history" style={{ gap: 6 }}>
        {list.map((s, i) => (
          <li key={s.id} className="status-edit-row">
            <input type="color" value={s.color ?? '#7A90A3'} onChange={(e) => run(() => send(`/api/v1/ops/statuses/${s.id}`, 'PATCH', { color: e.target.value }))} aria-label={`${s.name} colour`} />
            <button type="button" className="cx-title-btn" style={{ fontSize: 13 }} onClick={() => { const n = prompt('Rename status', s.name); if (n) run(() => send(`/api/v1/ops/statuses/${s.id}`, 'PATCH', { name: n })); }}>{s.name}</button>
            <span className="cx-label">{CAT[s.category]}</span>
            <button type="button" className="cx-link-btn" disabled={i === 0} onClick={() => run(async () => { await send(`/api/v1/ops/statuses/${s.id}`, 'PATCH', { position: list[i - 1]!.position }); await send(`/api/v1/ops/statuses/${list[i - 1]!.id}`, 'PATCH', { position: s.position }); })}>↑</button>
            <Select className="cx-select--sm" value="" aria-label={`Delete ${s.name}`} onChange={(e) => e.target.value && run(() => send(`/api/v1/ops/statuses/${s.id}?moveTo=${e.target.value}`, 'DELETE'))}>
              <option value="">Delete…</option>
              {list.filter((x) => x.id !== s.id).map((x) => <option key={x.id} value={x.id}>move tasks to {x.name}</option>)}
            </Select>
          </li>
        ))}
      </ul>
      <form style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 6 }} onSubmit={(e: FormEvent) => { e.preventDefault(); if (name) run(async () => { await send(`/api/v1/ops/spaces/${spaceId}/statuses`, 'POST', { name, category }); setName(''); }); }}>
        <Input placeholder="New status" value={name} onChange={(e) => setName(e.target.value)} data-testid="status-new-name" />
        <Select className="cx-select--sm" value={category} onChange={(e) => setCategory(e.target.value)}>{Object.entries(CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>
        <button type="submit" className="cx-btn cx-btn--sm">Add</button>
      </form>
      {err && <div className="cx-note cx-note--fire">{err}</div>}
    </div>
  );
}
